import React, {
  useEffect,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle
} from 'react'

import classNames from 'classnames'
import { TipoOrdenacao as TipoOrdenacaoGenerico } from 'src/tipos'

import { IconeCarregando, IconeOrdenacao } from '../icones'
import { Paginacao } from '../tabela/paginacao'
import { usePaginacao } from '../tabela/paginacao-hooks'
import { TabelaPaginacaoProvider } from '../tabela/paginacao-provider'
import Linha from './linha'
import {
  Componente,
  Tabela as ComponenteTabela,
  DadoFake,
  ModalCarregamento,
  SemDados,
  Cabecalho,
  BotaoOrdenacao,
  Informacao
} from './styles'
import { TabelaProps, TabelaDados, TabelaDinamicaRef } from './tipos'

const InstanciaTabela: React.ForwardRefExoticComponent<TabelaProps> = forwardRef<
  TabelaDinamicaRef,
  TabelaProps
>(
  (
    {
      Id,
      Colunas,
      Abas,
      ObterDados,
      RenderizarSemDados,
      ComPaginacao,
      MostrarCarregandoInstantaneo,
      TextoSemResultados,
      TemaLinha,
      className
    },
    ref: any
  ) => {
    const cancelado = React.useRef(false)
    const [carregando, definirCarregando] = useState<boolean>(true)
    const [dados, definirDados] = useState<TabelaDados[]>([])
    const [
      { Chave: ChaveOrdenacao, TipoOrdenacao },
      definirOrdenacao
    ] = useState<{ Chave?: string; TipoOrdenacao?: TipoOrdenacaoGenerico }>({
      Chave: undefined,
      TipoOrdenacao: undefined
    })

    const [{ Pagina, TamanhoPagina }, paginacaoDispatch] = usePaginacao()

    const irParaPagina = (pagina = 1) => {
      paginacaoDispatch({ tipo: 'ir-para-pagina', pagina })
    }

    const carregarDados = async (carregandoInstantaneo?: boolean) => {
      let carregandoId: number

      if (!carregandoInstantaneo) {
        carregandoId = window.setTimeout(() => {
          if (!cancelado.current) definirCarregando(true)
        }, 3000)
      } else {
        if (!cancelado.current) definirCarregando(true)
      }

      if (ComPaginacao) {
        paginacaoDispatch({ tipo: 'alterar-totais', totalRegistros: undefined })
      }

      try {
        const resposta = await ObterDados(
          Pagina,
          TamanhoPagina,
          ChaveOrdenacao,
          TipoOrdenacao as TipoOrdenacaoGenerico
        )
        if (TemaLinha && !!resposta?.Dados) {
          definirDados(
            resposta.Dados.map(i => {
              i.tema = TemaLinha(i)
              return i
            })
          )
        }
        if (cancelado.current) return

        if (!carregandoInstantaneo) {
          clearTimeout(carregandoId)
        }

        if (!resposta?.Dados || resposta.Dados.length === 0) {
          definirDados([])
          return
        }

        definirDados(resposta.Dados)

        if (ComPaginacao) {
          paginacaoDispatch({
            tipo: 'alterar-totais',
            totalRegistros: resposta.Paginacao.TotalRegistros
          })
          paginacaoDispatch({
            tipo: 'ir-para-pagina',
            pagina: resposta.Paginacao.Pagina
          })
        }
      } finally {
        if (!cancelado.current) definirCarregando(false)
      }
    }

    useEffect(() => {
      return () => {
        cancelado.current = true
      }
    }, [])

    useEffect(() => {
      carregarDados(MostrarCarregandoInstantaneo)
    }, [Pagina, TamanhoPagina, ChaveOrdenacao, TipoOrdenacao])

    const manipularOrdenacao = (Chave: string) => {
      if (Chave === ChaveOrdenacao) {
        definirOrdenacao(old => ({
          Chave,
          TipoOrdenacao: old.TipoOrdenacao === 1 ? 0 : 1
        }))
        return
      }

      definirOrdenacao({
        Chave,
        TipoOrdenacao: 1
      })
    }

    const renderizarCabecalho = () => (
      <tr>
        {(carregando || dados.length > 0) && <th style={{ width: '100px' }} />}
        {Colunas.map(
          (
            {
              Chave,
              Texto,
              Alinhamento,
              ComOrdenacao,
              RenderizarCabecalho,
              StyleColuna
            },
            indiceColuna: number
          ) => (
            <th
              key={`coluna-${indiceColuna}`}
              align={Alinhamento || 'left'}
              style={StyleColuna}
            >
              <Cabecalho>
                <Informacao comOrdenacao={!!ComOrdenacao}>
                  {RenderizarCabecalho ? (
                    RenderizarCabecalho()
                  ) : (
                    <label>{Texto}</label>
                  )}
                </Informacao>
                {!carregando && ComOrdenacao && (
                  <BotaoOrdenacao
                    type="button"
                    className={classNames({
                      asc: ChaveOrdenacao === Chave && TipoOrdenacao === 0,
                      desc: ChaveOrdenacao === Chave && TipoOrdenacao === 1,
                      customizado: !!RenderizarCabecalho
                    })}
                    onClick={(
                      e: React.MouseEvent<HTMLButtonElement, MouseEvent>
                    ) => {
                      e.preventDefault()
                      manipularOrdenacao(Chave)
                    }}
                  >
                    {IconeOrdenacao}
                  </BotaoOrdenacao>
                )}
              </Cabecalho>
            </th>
          )
        )}
      </tr>
    )
    const renderizarCarregando = useCallback(
      () =>
        [...Array(TamanhoPagina)].map((_, indiceLinha: number) => (
          <tr
            key={`linha-${indiceLinha}`}
            className={classNames({
              par: (indiceLinha + 1) % 2 === 0
            })}
          >
            <td key={`linha-${indiceLinha}-coluna-marcacao`}>
              <DadoFake />
            </td>
            {Colunas.map((Coluna, indiceColuna: number) => (
              <td
                key={`linha-${indiceLinha}-coluna-${indiceColuna}`}
                style={Coluna.StyleColuna}
              >
                <DadoFake />
              </td>
            ))}
          </tr>
        )),
      [Colunas, TamanhoPagina]
    )

    const renderizarModalCarregamento = () => (
      <ModalCarregamento>
        {IconeCarregando}
        <p>Carregando ...</p>
      </ModalCarregamento>
    )

    useImperativeHandle<unknown, TabelaDinamicaRef>(ref, () => ({
      ObterDados: () => dados,
      CarregarDados: carregarDados,
      IrParaPagina: irParaPagina
    }))

    const renderizarTabelaVazia = () =>
      RenderizarSemDados ? (
        RenderizarSemDados()
      ) : (
        <SemDados>
          {TextoSemResultados ??
            'Nenhum resultado encontrado para este filtro.'}
        </SemDados>
      )

    return (
      <Componente className={className}>
        <ComponenteTabela id={Id}>
          <thead>{renderizarCabecalho()}</thead>
          <tbody>
            {!carregando &&
              dados.map((linha: any, indice: number) => (
                <Linha
                  key={`linha-${indice}`}
                  Id={Id}
                  Indice={indice}
                  Dados={linha}
                  Colunas={Colunas}
                  AbasTabela={Abas}
                  TemaLinha={linha.tema}
                />
              ))}
            {carregando && renderizarCarregando()}
          </tbody>
        </ComponenteTabela>
        {!carregando && dados.length === 0 && renderizarTabelaVazia()}
        {carregando && renderizarModalCarregamento()}
        {ComPaginacao && <Paginacao />}
      </Componente>
    )
  }
)

export const TabelaDinamica = forwardRef<TabelaDinamicaRef, TabelaProps>(
  ({ TamanhoPagina, EstiloPaginacao, ...rest }, ref: any) => (
    <TabelaPaginacaoProvider
      TamanhoPagina={TamanhoPagina || 10}
      Estilo={EstiloPaginacao || 'Simples'}
    >
      <InstanciaTabela {...rest} ref={ref} />
    </TabelaPaginacaoProvider>
  )
)

export * from './tipos'
